import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  AlertTriangle,
  Boxes,
  CalendarClock,
  ReceiptText,
  ShoppingCart,
  WalletCards,
} from "lucide-react";
import {
  Card,
  Money,
  PageHeader,
} from "../components/ui";
import { db } from "../lib/db";
import { useAuth } from "../lib/auth";

interface Dashboard {
  revenue: number;
  grossRevenue?: number;
  refundedRevenue?: number;
  orderCount: number;
  estimatedProfit: number | null;
  profitVisible?: boolean;
  accounts: Array<{
    id: string;
    name: string;
    type: "CASH" | "BANK";
    balance: number;
  }>;
  alerts: {
    lowStock: number;
    outOfStock?: number;
    expiringLots?: number;
    nearExpiry?: {
      totalLots: number;
      criticalLots: number;
      warningLots: number;
      attentionLots: number;
      skuCount: number;
      totalQuantity: number;
    };
    expired?: {
      totalLots: number;
      skuCount: number;
      totalQuantity: number;
    };
    pendingPayments: number;
  };
}

export function HomePage() {
  const auth = useAuth();
  const role = auth.session?.user.role ?? "OWNER";
  const canSell = ["OWNER", "MANAGER", "STAFF"].includes(role);
  const canReceive = ["OWNER", "MANAGER", "STAFF"].includes(role);
  const canClose = ["OWNER", "MANAGER"].includes(role);
  const dashboard = useLiveQuery(
    async () => {
      const record =
        await db.meta.get("dashboard");
      return record?.value as
        | Dashboard
        | undefined;
    },
    [],
  );

  const cash =
    dashboard?.accounts.find(
      (account) =>
        account.type === "CASH",
    )?.balance ?? 0;
  const bank =
    dashboard?.accounts.find(
      (account) =>
        account.type === "BANK",
    )?.balance ?? 0;

  const attentionCount =
    (dashboard?.alerts.lowStock ?? 0) +
    (dashboard?.alerts.outOfStock ?? 0) +
    (dashboard?.alerts.expired?.totalLots ?? 0) +
    (dashboard?.alerts.nearExpiry?.totalLots ?? dashboard?.alerts.expiringLots ?? 0) +
    (dashboard?.alerts.pendingPayments ?? 0);

  return (
    <div className="page-stack">
      <PageHeader
        title="Hôm nay"
        description="Tổng quan nhanh hoạt động cửa hàng"
      />

      <Card className="hero-metric">
        <span>Doanh thu thuần hôm nay</span>
        <Money
          value={dashboard?.revenue ?? 0}
          className="money-hero"
        />

        <div className="hero-submetrics">
          <div>
            <small>
              Lợi nhuận tạm tính
            </small>
            {dashboard?.profitVisible === false ? (
              <strong>Ẩn theo quyền</strong>
            ) : (
              <Money
                value={
                  dashboard?.estimatedProfit ??
                  0
                }
              />
            )}
          </div>

          <div>
            <small>Số đơn</small>
            <strong>
              {dashboard?.orderCount ?? 0}
            </strong>
          </div>
        </div>

        {(dashboard?.refundedRevenue ?? 0) >
        0 ? (
          <small>
            Đã trừ hoàn hàng:{" "}
            {dashboard?.refundedRevenue?.toLocaleString(
              "vi-VN",
            )}{" "}
            ₫
          </small>
        ) : null}
      </Card>

      {canSell ? (
        <Link
          to="/sales"
          className="btn btn-primary btn-sales-home"
        >
          <ShoppingCart size={22} /> + BÁN HÀNG
        </Link>
      ) : (
        <Link
          to="/sales/history"
          className="btn btn-secondary btn-sales-home"
        >
          <ReceiptText size={22} /> XEM ĐƠN HÀNG
        </Link>
      )}

      <div className="metric-grid">
        <Card>
          <WalletCards size={20} />
          <span>Tiền mặt</span>
          <Money value={cash} />
        </Card>

        <Card>
          <ReceiptText size={20} />
          <span>Chuyển khoản</span>
          <Money value={bank} />
        </Card>
      </div>

      <div className="quick-grid">
        {canReceive ? (
          <Link
            to="/inventory/receive"
            className="quick-action"
          >
            <Boxes size={22} />
            <span>Nhập hàng</span>
          </Link>
        ) : null}

        <Link
          to="/cash"
          className="quick-action"
        >
          <WalletCards size={22} />
          <span>Ghi thu / chi</span>
        </Link>

        {canClose ? (
          <Link
            to="/closing"
            className="quick-action"
          >
            <ReceiptText size={22} />
            <span>Đóng sổ ngày</span>
          </Link>
        ) : null}
      </div>

      <section>
        <div className="section-title">
          <div>
            <AlertTriangle size={20} />
            <h2>Cần chú ý</h2>
          </div>
          <Link to="/alerts">
            Xem tất cả
          </Link>
        </div>

        <Card className="alert-summary">
          {attentionCount === 0 ? (
            <div className="all-good">
              Không có việc khẩn cấp cần xử
              lý.
            </div>
          ) : (
            <>
              {(dashboard?.alerts.outOfStock ?? 0) > 0 ? (
                <Link to="/inventory">
                  <span className="alert-dot danger" style={{ backgroundColor: "#ef4444" }} />
                  <div>
                    <strong style={{ color: "#b91c1c" }}>
                      {dashboard?.alerts.outOfStock} sản phẩm hết hàng
                    </strong>
                    <small>Cần nhập hàng ngay để không gián đoạn bán hàng.</small>
                  </div>
                  <span>Kiểm tra</span>
                </Link>
              ) : null}

              {((dashboard?.alerts.expired?.totalLots ?? 0) > 0) ? (
                <Link to="/alerts">
                  <span className="alert-dot danger" style={{ backgroundColor: "#dc2626" }} />
                  <div>
                    <strong style={{ color: "#991b1b" }}>
                      {dashboard?.alerts.expired?.totalLots} lô đã hết hạn ({dashboard?.alerts.expired?.totalQuantity} tồn)
                    </strong>
                    <small>Đã loại khỏi bán hàng tự động (FEFO). Cần lập phiếu điều chỉnh/tiêu hủy.</small>
                  </div>
                  <CalendarClock size={18} />
                </Link>
              ) : null}

              {((dashboard?.alerts.nearExpiry?.totalLots ?? 0) > 0) ? (
                <Link to="/alerts">
                  <span className="alert-dot warning" />
                  <div>
                    <strong>
                      {dashboard?.alerts.nearExpiry?.totalLots} lô sắp hết hạn
                      {dashboard?.alerts.nearExpiry?.criticalLots ? ` (${dashboard.alerts.nearExpiry.criticalLots} lô ≤ 7 ngày)` : ""}
                    </strong>
                    <small>
                      Ưu tiên bán theo FEFO ({dashboard?.alerts.nearExpiry?.totalQuantity} đơn vị, {dashboard?.alerts.nearExpiry?.skuCount} SKU).
                    </small>
                  </div>
                  <CalendarClock size={18} />
                </Link>
              ) : (dashboard?.alerts.expiringLots ?? 0) > 0 ? (
                <Link to="/alerts">
                  <span className="alert-dot warning" />
                  <div>
                    <strong>{dashboard?.alerts.expiringLots} lô sắp/đã hết hạn</strong>
                    <small>Ưu tiên xử lý theo FEFO và kiểm tra hàng quá hạn.</small>
                  </div>
                  <CalendarClock size={18} />
                </Link>
              ) : null}

              {(dashboard?.alerts.lowStock ?? 0) > 0 ? (
                <Link to="/inventory">
                  <span className="alert-dot warning" />
                  <div>
                    <strong>{dashboard?.alerts.lowStock} sản phẩm sắp hết</strong>
                    <small>Kiểm tra tồn kho và nhập thêm nếu cần.</small>
                  </div>
                  <span>Kiểm tra</span>
                </Link>
              ) : null}

              {(dashboard?.alerts
                .pendingPayments ??
                0) > 0 ? (
                <Link to="/cash">
                  <span className="alert-dot info" />
                  <div>
                    <strong>
                      {
                        dashboard?.alerts
                          .pendingPayments
                      }{" "}
                      thanh toán cần đối chiếu
                    </strong>
                    <small>
                      Chưa nên xem là tiền đã
                      nhận.
                    </small>
                  </div>
                  <span>Kiểm tra</span>
                </Link>
              ) : null}
            </>
          )}
        </Card>
      </section>
    </div>
  );
}
